// @flow

import React from 'react';
import type { MapViewer } from '../../../../types/VisioglobeType';
import geminiApi from '../../../../network/api/geminiApi';
import { getCurrentPcId } from '../../../../utils/urlUtils';
import type { ReconciliationStore } from '../../../../types/shoppingCenterType';
import {
  emptyUnitColor,
  manuallyFlagged,
  manuallyMapped,
  multipleLux,
  reconciliationAction,
  reconciliationStates,
  retailerMissingColor,
  transitionningStage,
  unMappedColor,
} from '../../../../const';
import LeftDataMappingMode from './LeftDataMappingMode';
import RightActionMappingMode from './RightActionMappingMode';
import reconciliationApi from '../../../../network/api/reconciliationApi';

import VisioGlobeUI from '../../../common/VisioGlobeUI';
import { getPossibleStoresList, getStoreLists } from './reconciliationUtils';
import { notifyApolloError } from '../../../../network/notification';

type Props = {
  dispatch: () => void,
  changeMappingMode: () => void,
  preselectedStore: ReconciliationStore,
  mapviewer: MapViewer,
};

type State = {
  storeList: ReconciliationStore[],
  historyStoreList: ReconciliationStore[],
  selectedStore: ReconciliationStore,
  isLoading: boolean,
};

class MappingMode extends React.Component<Props, State> {
  state = {
    storeList: [],
    historyStoreList: [],
    selectedStore: null,
  };

  mapColorEmptyStores = (storeList: string[]) => storeList.map(store => ({
    storeIds: store.idVisioglobes,
    color: '',
    storeName: store.mercuryName || '',
  }));

  updateColor = (storeList: ReconciliationStore[]) => {
    this.props.mapviewer.updateColors(storeList.map((store) => {
      const storeColor = {
        storeIds: store.idVisioglobes,
        color: unMappedColor,
        storeName: store.mercuryName || store.luxName || store.retailerName || '',
      };

      if (store.state === reconciliationStates.MULTIPLE_LUX) {
        storeColor.color = multipleLux;
        return storeColor;
      }

      if (store.state === reconciliationStates.EMPTY_UNIT) {
        storeColor.color = emptyUnitColor;
        return storeColor;
      }

      if (store.state === reconciliationStates.RETAILER_MISSING) {
        storeColor.color = retailerMissingColor;
        return storeColor;
      }

      if (store.state === reconciliationStates.LUX_ONLY) {
        storeColor.color = transitionningStage;
        return storeColor;
      }

      if (store.state === reconciliationStates.MAPPED) {
        storeColor.color = manuallyMapped;
        return storeColor;
      }

      if (store.state === reconciliationStates.FLAGGED) {
        storeColor.color = manuallyFlagged;
        return storeColor;
      }

      return storeColor;
    }));
  };

  componentDidMount() {
    this.props.mapviewer.setSelectStoreCallback(this.selectStoreCallback);
    this.updateStoreMapping(this.props.preselectedStore);
  }

  preselectStoreIfPossible = (preselectedStore?: ReconciliationStore) => {
    const displayedIdVisioglobes = this.state.storeList.filter(store =>
      store.state !== reconciliationStates.OK)
      .flatMap(store => store.idVisioglobes);

    if (preselectedStore
      && (displayedIdVisioglobes.includes(preselectedStore.idVisioglobes[0]))
    ) {
      this.updateSelectedStore(preselectedStore.idVisioglobes[0], preselectedStore);
    }
  }

  updateStoreMapping = (preselectedStore?: ReconciliationStore) => {
    this.setState({
      isLoading: true,
    });
    geminiApi.fetchReconciliation(getCurrentPcId())
      .then(response => response.json())
      .then((storeList) => {
        const { displayedStoreList, historyStoreList } = getStoreLists(storeList);
        this.setState({
          storeList: displayedStoreList,
          historyStoreList,
          selectedStore: null,
        });
        this.props.mapviewer.setMappingMode(true);
        this.props.mapviewer.loadLabels(
          storeList,
          this.mapColorEmptyStores(storeList),
        );
        this.updateColor(displayedStoreList.filter(store =>
          store.state !== reconciliationStates.OK));
      })
      .finally(() => {
        this.setState({
          isLoading: false,
        });
        this.preselectStoreIfPossible(preselectedStore);
      });
  };

  saveStoreMapping = (
    reconciliationStore: ReconciliationStore,
    idStoreCms ?: string,
    idVisioglobes ?: string[],
    type: string,
  ) => {
    const reconciliationStoreMappingBean =
      {
        idReconciliation: reconciliationStore.id,
        idStoreCms: idStoreCms || reconciliationStore.idStoreCms,
        idMercury: reconciliationStore.idMercury,
        idVisioglobes: idVisioglobes || reconciliationStore.idVisioglobes,
      };

    reconciliationApi.saveStoreMapping(getCurrentPcId(), type, reconciliationStoreMappingBean)
      .catch(e => this.props.dispatch(notifyApolloError(e)))
      .finally(this.updateStoreMapping);
  }

  unMap = (reconciliationStore: ReconciliationStore) => {
    const reconciliationStoreMappingBean =
      {
        idReconciliation: reconciliationStore.id,
        idStoreCms: reconciliationStore.idStoreCms,
        idMercury: reconciliationStore.idMercury,
        idVisioglobes: reconciliationStore.idVisioglobes,
      };
    reconciliationApi.saveStoreMapping(getCurrentPcId(), reconciliationAction.UNMAP, reconciliationStoreMappingBean)
      .catch(e => this.props.dispatch(notifyApolloError(e)))
      .finally(this.updateStoreMapping);
  }

  selectStoreCallback = (store: ReconciliationStore) => {
    this.setState({ selectedStore: store });
  };

  updateSelectedStore = (storeIdVisioglobe, store) => {
    this.props.mapviewer
      .selectPlace(
        storeIdVisioglobe,
        () => this.forceUpdate(),
      );
    this.setState({ selectedStore: store });
  };

  resetSelectedStore = () => {
    this.props.mapviewer
      .selectPlace(
        null,
        () => this.forceUpdate(),
      );
    this.setState({ selectedStore: null });
  };

  getHistoryStoreByIdVisioglobe = () => {
    const historyStore = this.state.historyStoreList.filter(history =>
      history.idVisioglobes.includes(this.state.selectedStore.idVisioglobes));
    if (historyStore) {
      return historyStore.retailerName;
    }
    return null;
  }

  render() {
    return (
      <>
        <LeftDataMappingMode
          changeMappingMode={this.props.changeMappingMode}
          storeList={this.state.storeList}
          updateSelectedStore={this.updateSelectedStore}
        />
        {this.state.selectedStore
        && (this.state.selectedStore.state !== reconciliationStates.OK)
        && (
          <RightActionMappingMode
            selectedStore={this.state.selectedStore}
            possibleStoresList={getPossibleStoresList(
              this.state.storeList,
              this.state.selectedStore,
            )}
            oldTenant={this.getHistoryStoreByIdVisioglobe()}
            saveStoreMapping={this.saveStoreMapping}
            unMap={this.unMap}
            isLoading={this.state.isLoading}
            unselectStore={this.resetSelectedStore}
          />
        )
        }
        <div className="bottom-menu-gemini-container">
          <div className="bottom-menu-container mapping-legend">
            <VisioGlobeUI mapviewer={this.props.mapviewer} />
          </div>
        </div>
      </>
    );
  }
}

export default MappingMode;
